import { useMutation } from '@tanstack/react-query';
import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';

type UploadArgs = {
  file: File;
  moduleName?: string;
  signal?: AbortSignal;
};

async function getDimensions(file: File) {
  if (file.type.startsWith('image/')) {
    return new Promise<{ width: number; height: number; orientation: string }>(
      (resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.src = url;
        img.onload = () => {
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          URL.revokeObjectURL(url);
          resolve({
            width: w,
            height: h,
            orientation: h > w ? 'PORTRAIT' : w > h ? 'LANDSCAPE' : 'SQUARE',
          });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve({ width: 0, height: 0, orientation: 'error' });
        };
      },
    );
  }
  if (file.type.startsWith('video/')) {
    return new Promise<{ width: number; height: number; orientation: string }>(
      (resolve) => {
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);
        
        let resolved = false;
        const cleanupAndResolve = (result: { width: number; height: number; orientation: string }) => {
          if (resolved) return;
          resolved = true;
          URL.revokeObjectURL(url);
          resolve(result);
        };

        const timer = setTimeout(() => {
          cleanupAndResolve({ width: 0, height: 0, orientation: 'unknown' });
        }, 4000);

        video.preload = 'metadata';
        video.src = url;
        video.onloadedmetadata = () => {
          clearTimeout(timer);
          const w = video.videoWidth;
          const h = video.videoHeight;
          cleanupAndResolve({
            width: w,
            height: h,
            orientation: h > w ? 'PORTRAIT' : w > h ? 'LANDSCAPE' : 'SQUARE',
          });
        };
        video.onerror = () => {
          clearTimeout(timer);
          cleanupAndResolve({ width: 0, height: 0, orientation: 'error' });
        };
      },
    );
  }
  return { width: 0, height: 0, orientation: 'unknown' };
}

export function useUploadSingleFile() {
  return useMutation({
    mutationFn: async ({ file, moduleName, signal }: UploadArgs) => {
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE) || 1;
      const filename = file.name;
      const mimetype = file.type;
      const fileSize = file.size;
      const dimensions = await getDimensions(file);

      const { data: initResponse } = await httpClient.post(
        endpoints.upload.chunked,
        {
          action: 'init',
          filename,
          mimetype,
          fileSize,
          moduleName,
          sessionId: localStorage.getItem('SESSION_ID'),
        },
        { signal },
      );

      if (!initResponse?.success || !initResponse?.data?.uploadId) {
        throw new Error(initResponse?.message || 'Failed to initialize upload');
      }

      const uploadId = initResponse.data.uploadId as string;
      const possibleMediaId = initResponse.data.mediaId as string | undefined;

      for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
        const start = (chunkNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBlob = file.slice(start, end);
        const chunk = new Blob([chunkBlob], { type: mimetype });

        const formData = new FormData();
        formData.append('action', 'upload');
        formData.append('uploadId', uploadId);
        formData.append('chunkNumber', String(chunkNumber));
        formData.append('totalChunks', String(totalChunks));
        formData.append('chunk', chunk, filename);

        const { data: chunkResponse } = await httpClient.post(
          endpoints.upload.chunked,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 0,
            signal,
          },
        );

        if (!chunkResponse?.success) {
          throw new Error(
            chunkResponse?.message || `Failed to upload chunk ${chunkNumber}`,
          );
        }
      }

      const { data: completeResponse } = await httpClient.post(
        endpoints.upload.chunked,
        {
          action: 'complete',
          uploadId,
          totalSize: fileSize,
        },
        { signal },
      );

      if (!completeResponse?.success || !completeResponse?.data?.mediaId) {
        if (possibleMediaId) {
          return {
            success: true,
            data: {
              mediaId: possibleMediaId,
              filename,
              mediaType: mimetype,
              size: fileSize,
            },
            dimensions,
          };
        }
        throw new Error(
          completeResponse?.message || 'Failed to complete upload',
        );
      }

      return { ...completeResponse, dimensions };
    },
  });
}
